// ==================== Enums & Constants ====================
export type OrgRole = 'owner' | 'admin' | 'manager' | 'member';
export type GlobalRole = 'global_admin' | 'user';
export type UserStatus = 'invited' | 'active' | 'deactivated';
export type ProjectStatus = 'active' | 'archived' | 'completed';
export type ApprovalStatus = 'unsubmitted' | 'pending' | 'approved' | 'rejected';
export type TimesheetStatus = 'draft' | 'submitted' | 'approved' | 'rejected';
export type PlanTier = 'free' | 'starter' | 'professional' | 'enterprise';
export type NotificationType = 'timesheet_reminder' | 'timesheet_submitted' | 'timesheet_approved' | 'timesheet_rejected' | 'org_invitation' | 'member_joined' | 'project_assigned' | 'budget_warning' | 'timer_reminder' | 'system';

// Legacy compat
export type Role = 'admin' | 'editor' | 'viewer' | 'lendee' | 'owner' | 'manager' | 'member';

export const ORG_ROLE_HIERARCHY: OrgRole[] = ['member', 'manager', 'admin', 'owner'];

export const ORG_ROLE_PERMISSIONS: Record<OrgRole, string[]> = {
  member: ['track_own_time', 'view_own_entries', 'submit_timesheet'],
  manager: ['track_own_time', 'view_own_entries', 'submit_timesheet', 'view_team_entries', 'approve_timesheets'],
  admin: ['track_own_time', 'view_own_entries', 'submit_timesheet', 'view_team_entries', 'approve_timesheets', 'manage_projects', 'manage_clients', 'manage_users', 'view_reports'],
  owner: ['track_own_time', 'view_own_entries', 'submit_timesheet', 'view_team_entries', 'approve_timesheets', 'manage_projects', 'manage_clients', 'manage_users', 'view_reports', 'manage_billing', 'manage_org'],
};

// ==================== Role Types ====================
export interface UserRole {
  userId: string;
  orgId: string;
  role: OrgRole | 'super_admin';
  grantedBy?: string;
  grantedAt: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OrgMembership {
  orgId: string;
  roles: OrgRole[];
  grantedAt: string;
}

// ==================== User Types ====================
export interface User {
  id: string;
  email: string;
  username?: string;
  firstName: string;
  lastName: string;
  orgId?: string;
  orgRole: OrgRole;
  orgRoles?: OrgRole[];  // All roles in active org (multi-role)
  isSuperAdmin: boolean;
  role: Role;
  isActive: boolean;
  isVerified: boolean;
  status: UserStatus;
  timezone: string;
  defaultHourlyRate?: string;
  weeklyCapacity: number;
  avatarUrl?: string;
  phone?: string;
  notificationPreferences?: NotificationPreferences;
  oauthProviders?: Record<string, { provider_user_id: string; linked_at: string }>;
  mustResetPassword?: boolean;
  invitedAt?: string;
  orgMemberships?: OrgMembership[];  // All org memberships (multi-org)
  createdAt: string;
  updatedAt: string;
}

export interface NotificationPreferences {
  emailTimesheetReminder: boolean;
  emailApprovalNotification: boolean;
  inAppNotifications: boolean;
  desktopNotifications: boolean;
  timerReminder: boolean;
  timerReminderMinutes: number;
}

export interface UserPreferences {
  timezone: string;
  weeklyCapacity: number;
  defaultHourlyRate?: string;
  notificationPreferences: NotificationPreferences;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  accessToken?: string;
  user: User;
  challengeName?: string;
  session?: string;
  email?: string;
}

// ==================== Organization Types ====================
export interface Organization {
  id: string;
  name: string;
  slug: string;
  ownerId: string;
  planTier: PlanTier;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  logoUrl?: string;
  settings?: OrgSettings;
  memberCount: number;
  isActive: boolean;
  userRoles?: OrgRole[];  // User's roles in this org (returned by /me/orgs)
  createdAt: string;
  updatedAt: string;
}

export interface OrgSettings {
  timezone: string;
  weekStart: 'monday' | 'sunday';
  roundingIncrement: number; // minutes: 1, 5, 6, 10, 15, 30
  requireApproval: boolean;
  defaultBillableRate?: number;
}

export interface OrgInvite {
  id: string;
  orgId: string;
  email: string;
  role: OrgRole;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  invitedBy: string;
  expiresAt: string;
  acceptedAt?: string;
  createdAt: string;
}

// ==================== Client Types ====================
export interface Client {
  orgId: string;
  id: string;
  name: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ==================== Project Types ====================
export interface Project {
  orgId: string;
  id: string;
  name: string;
  code: string;
  clientId?: string;
  parentId?: string;
  description?: string;
  color: string;
  status: ProjectStatus;
  isBillable: boolean;
  hourlyRate?: string;
  budgetHours?: string;
  budgetAmount?: string;
  assignedUserIds?: string[];
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Task {
  orgId: string;
  id: string;
  projectId: string;
  name: string;
  description?: string;
  isBillable: boolean;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ==================== Time Entry Types ====================
export interface TimeEntry {
  orgId: string;
  id: string;
  userId: string;
  projectId?: string;
  taskId?: string;
  clientId?: string;
  date: string;
  description: string;
  durationMinutes: number;
  startTime?: string;
  endTime?: string;
  isBillable: boolean;
  isRunning: boolean;
  timerStartedAt?: string;
  source: 'manual' | 'timer' | 'import' | 'ai_suggest';
  approvalStatus: ApprovalStatus;
  approvedBy?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ActiveTimer {
  entry: TimeEntry;
  elapsedSeconds: number;
}

export interface CreateTimeEntryRequest {
  projectId?: string;
  taskId?: string;
  clientId?: string;
  date?: string;
  description: string;
  durationMinutes: number;
  startTime?: string;
  endTime?: string;
  isBillable?: boolean;
}

// ==================== Timesheet Types ====================
export interface TimesheetWeek {
  orgId: string;
  userId: string;
  weekStart: string;
  status: TimesheetStatus;
  totalHours: number;
  billableHours: number;
  submittedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

// ==================== Narrative Preset Types ====================
export interface NarrativePreset {
  orgId: string;
  id: string;
  userId?: string;
  projectId?: string;
  title: string;
  text: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ==================== Dashboard Types ====================
export interface DashboardStats {
  todayHours: number;
  weekHours: number;
  todayEntries: number;
  weekEntries: number;
  hasRunningTimer: boolean;
}

export interface OrgDashboardStats {
  organizationName: string;
  planTier: PlanTier;
  memberCount: number;
  projectCount: number;
  clientCount: number;
  activeProjects: number;
}

// ==================== Report Types ====================
export interface ReportFilter {
  startDate?: string;
  endDate?: string;
  projectId?: string;
  clientId?: string;
  userId?: string;
  billableOnly?: boolean;
}

export interface ReportResult {
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  entryCount: number;
  billablePercent: number;
}

// ==================== Billing Types ====================
export interface PlanDetails {
  id: string;
  name: string;
  tier: PlanTier;
  stripePriceId?: string;
  priceMonthly: number;
  priceYearly: number;
  maxMembers: number;
  maxProjects: number;
  features: string[];
  isActive: boolean;
}

export interface Subscription {
  planTier: PlanTier;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
}

// ==================== Notification Types ====================
export interface AppNotification {
  userId: string;
  id: string;
  orgId?: string;
  type: NotificationType;
  title: string;
  message: string;
  isRead: boolean;
  actionUrl?: string;
  createdAt: string;
}

// ==================== AI Chat Types ====================
export interface AIChatSession {
  userId: string;
  id: string;
  orgId: string;
  title: string;
  messageCount: number;
  lastMessageAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AIChatMessage {
  sessionId: string;
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  chartConfig?: Record<string, any>;
  createdAt: string;
}

// ==================== AI Model Config Types ====================
export type AIModelProvider = 'openai' | 'anthropic' | 'custom';

export interface AIModelConfig {
  id: string;
  name: string;
  modelId: string;
  provider: AIModelProvider;
  maxContext: number;
  isActive: boolean;
  isDefault: boolean;
  config: {
    temperature: number;
    maxTokens: number;
    hasApiKey: boolean;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface AIModelUpdateRequest {
  modelId: string;
  name?: string;
  provider?: string;
  model_name?: string;
  isActive?: boolean;
  isDefault?: boolean;
  apiKey?: string;
  config?: string | { temperature?: number; maxTokens?: number };
}

// ==================== Theme Config Types ====================
export interface ThemeConfig {
  colors: {
    primary: string;
    secondary: string;
    tertiary?: string;
    background?: string;
    paper?: string;
  };
  logo?: string;
  appName: string;
  favicon?: string;
  mode?: 'dark' | 'light';
}

// ==================== API Response Types ====================
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ==================== Audit Types ====================
export interface AuditLogEntry {
  id: string;
  userId: string;
  orgId?: string;
  action: string;
  resource: string;
  resourceId?: string;
  details?: Record<string, any>;
  timestamp: string;
  ipAddress?: string;
}

// ==================== RBAC Types ====================
export interface RBACPermission {
  resource: string;
  actions: string[];
}

export const ROLE_PERMISSIONS: Record<string, RBACPermission[]> = {
  admin: [{ resource: '*', actions: ['*'] }],
  owner: [{ resource: '*', actions: ['*'] }],
  manager: [
    { resource: 'time_entries', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'projects', actions: ['read'] },
    { resource: 'reports', actions: ['read'] },
    { resource: 'timesheets', actions: ['read', 'approve'] },
  ],
  member: [
    { resource: 'time_entries', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'projects', actions: ['read'] },
  ],
  editor: [{ resource: 'content', actions: ['create', 'read', 'update'] }],
  viewer: [{ resource: 'content', actions: ['read'] }],
  lendee: [{ resource: 'documents', actions: ['create', 'read', 'delete'] }],
};
